import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentTenantUserId } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import ConversationList from './ConversationList';
import ConversationView from './ConversationView';
import NewConversationModal from './NewConversationModal';
import './ChatWindow.css';

interface ChatWindowProps {
  onClose: () => void;
}

interface Conversation {
  _id: string;
  participants: { _id: string; name: string; email: string }[];
  lastMessage: {
    content: string;
    sender: string;
    timestamp: string;
  } | null;
  updatedAt: string;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  // Phase 1: the "new conversation" recipient picker (NewConversationModal)
  // lists users/members/groups via admin-only routes that now 403 for members.
  // There is no member-safe contacts endpoint yet, so we only expose the
  // "start a new conversation" affordance to admins. Members can still read and
  // reply to existing conversations (e.g. ones an admin started with them).
  // TODO(Phase 2): add a policy-gated GET /api/my/contacts endpoint that
  // returns the people a member is allowed to message, then re-enable this
  // affordance for members sourcing from that endpoint.
  const canStartConversation = isAdmin;
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleBack = () => {
    setActiveConversation(null);
    setRefreshKey(prev => prev + 1);
  };

  const handleNewConversation = () => {
    setShowNewConversation(true);
  };

  const handleConversationCreated = (conversation: Conversation) => {
    setShowNewConversation(false);
    setActiveConversation(conversation);
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="chat-window">
      <div className="chat-window__header">
        {activeConversation ? (
          <>
            <button className="chat-window__back" onClick={handleBack}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path fillRule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clipRule="evenodd" />
              </svg>
            </button>
            <span className="chat-window__title">
              {activeConversation.participants
                .filter(p => {
                  // participant._id is a tenant users.id, not the global id.
                  const userId = getCurrentTenantUserId();
                  return String(p._id) !== userId;
                })
                .map(p => p.name)
                .join(', ')}
            </span>
          </>
        ) : (
          <>
            <span className="chat-window__title">{t('messaging.title')}</span>
            {canStartConversation && (
              <button className="chat-window__new" onClick={handleNewConversation} title={t('messaging.newConversation')}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </>
        )}
        <button className="chat-window__close" onClick={onClose}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
      <div className="chat-window__content">
        {activeConversation ? (
          <ConversationView conversation={activeConversation} />
        ) : (
          <ConversationList
            key={refreshKey}
            onSelectConversation={setActiveConversation}
          />
        )}
      </div>
      {showNewConversation && canStartConversation && (
        <NewConversationModal
          onClose={() => setShowNewConversation(false)}
          onConversationCreated={handleConversationCreated}
        />
      )}
    </div>
  );
};

export default ChatWindow;
